/**
 * Hand-eased scroll glide. Native smooth-scroll gets cut short by concurrent
 * layout changes, so this re-measures its destination every frame — the
 * motion stays fluid while panels mount, animate, or typeset underneath it.
 *
 * Only one glide may own a container at a time. The draft's end-of-stream
 * glide and the list's end-of-generation glide overlap by design (the last
 * token lands seconds before the pipeline reports done), and two rAF loops
 * writing scrollTop on alternate frames fought to a different resting place
 * each run — the "sometimes it stops short" of UAT 2026-09-21. Claiming the
 * container cancels whatever was mid-flight, so the newest intent wins.
 */

const glideOwner = new WeakMap<HTMLElement, number>();
let glideSequence = 0;

/** Claim the container; returns a check that goes false once someone else claims it. */
function claim(container: HTMLElement): () => boolean {
  const id = ++glideSequence;
  glideOwner.set(container, id);
  return () => glideOwner.get(container) === id;
}

const ease = (t: number) => 1 - Math.pow(1 - t, 3);
const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Animate scrollTop toward a destination re-measured every frame. */
function glide(container: HTMLElement, destination: () => number, ms: number): void {
  const owns = claim(container);
  if (prefersReducedMotion()) {
    container.scrollTop = destination();
    return;
  }
  const start = container.scrollTop;
  const t0 = performance.now();
  const step = (now: number) => {
    if (!owns()) return;
    const p = Math.min(1, (now - t0) / ms);
    container.scrollTop = start + (destination() - start) * ease(p);
    if (p < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/** Ease the panel back to its very top. Both glides in a generation — the
 *  draft's at the last token and the list's at completion — land here, so
 *  the reader sees one move and the finished answer reads from the top. */
export function glideToTop(container: HTMLElement, opts?: { ms?: number }): void {
  glide(container, () => 0, opts?.ms ?? 650);
}
